import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import ExecutionEnvironment from 'exenv';

import { cn } from 'helpers/classnames';
import shippingBox from 'images/shipping-box.svg';
import type { AppState } from 'types/app';
import type { ProductStyle } from 'types/cloudCatalog';
import type { FormattedProductBundle } from 'reducers/detail/productDetail';
import useMartyContext from 'hooks/useMartyContext';
import { onEvent } from 'helpers/EventHelpers';
import { constructMSAImageUrl } from 'helpers/index';
import { generateRetinaImageParams } from 'helpers/ProductUtils';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { isDesktop } from 'helpers/ClientUtils';
import { ADD_TO_CART_BUTTON_ID } from 'constants/addToCart';

import css from 'styles/components/productdetail/addToCartSticky.scss';
// eslint-disable-next-line css-modules/no-unused-class
import cssAddToCart from 'styles/components/productdetail/addToCart.scss';

interface Props {
  addToCartText: string;
  isAppAdvertisementShowing: boolean;
  productDetail?: FormattedProductBundle;
  inView: boolean;
  entry: IntersectionObserverEntry | undefined;
  isDisabled: boolean | undefined;
  isSelectionValid: boolean;
  stockId: string | undefined;
  productStyle: ProductStyle;
  onHideSelectSizeTooltip: () => void;
  onShowSelectSizeTooltip: () => void;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>, isSticky: boolean, isStickyBelowFold: boolean) => void;
}

/**
 * PDP Sticky Add To Cart component ids
 */
const CONFIG = {
  STICKY_PARENT: 'root', // The parent node for the sticky add to cart
  BUY_BOX_FORM: 'buyBoxForm', // The buy box form
  SIZE_SELECTION: 'sizingChooser' // The container for the sizing options
};

/**
 * PDP Sticky Add To Cart Button
 * @returns {FunctionComponent}
 */
const AddToCartSticky: FunctionComponent<Props> = ({
  inView,
  entry,
  addToCartText,
  productDetail,
  isDisabled,
  stockId,
  productStyle,
  isSelectionValid,
  isAppAdvertisementShowing,
  onAddToCart,
  onShowSelectSizeTooltip,
  onHideSelectSizeTooltip
}) => {
  const {
    preventOnTouchDevice,
    testId,
    marketplace: { pdp: pdpConfig }
  } = useMartyContext();
  // Retrieves the featured image
  const featuredImage = productStyle.images?.[0] || false;

  // Figures out pricing
  const { originalPrice, percentOff, price } = productStyle;
  const hasDiscount = percentOff !== '0%';

  // Mounts the sticky add to cart button on the "root" div
  const addToCartStickyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const martyRoot = document.getElementById(CONFIG.STICKY_PARENT);
    const addToCartStickyNode = addToCartStickyRef.current;
    if (martyRoot && addToCartStickyNode) {
      martyRoot.appendChild(addToCartStickyNode);
    }
    return () => {
      if (martyRoot && addToCartStickyNode) {
        martyRoot.removeChild(addToCartStickyNode);
      }
    };
  }, [addToCartStickyRef]);

  // Checks upper boundary
  const [upperBoundary, setUpperBoundary] = useState(false);
  const [isPageOffset, setIsPageOffset] = useState(false);
  const checkUpperBoundary = useCallback(
    debounce(() => {
      const addToCartStickyNode = addToCartStickyRef.current;
      if (addToCartStickyNode) {
        const topDistance = window.pageYOffset + addToCartStickyNode.getBoundingClientRect().top;
        setUpperBoundary(topDistance > 0 && topDistance < 290);

        /*
          isPageOffset hasn't been set yet
          and pageYOffset is greater than 0
        */
        if (!isPageOffset && window.pageYOffset) {
          setIsPageOffset(true);
        }
      }
    }, 250),
    []
  );

  useEffect(() => {
    const upperBoundaryScrollHandler = onEvent(window, 'scroll', checkUpperBoundary);
    const resizeHandler = onEvent(window, 'resize', checkUpperBoundary);
    return () => {
      upperBoundaryScrollHandler.unbind();
      resizeHandler.unbind();
    };
  }, [checkUpperBoundary]);

  // Handles add to cart action
  const addToCartAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isSelectionValid || isDisabled) {
      // 1 - If user is missing selections, scrolls smoothly the user to it
      // 2 - If the item is auto of stock, scrolls with no animation to it (the out of stock modal actually break the animation)
      document.getElementById(CONFIG.SIZE_SELECTION)?.scrollIntoView({
        behavior: isDisabled ? 'auto' : 'smooth',
        block: 'center'
      });
    } else {
      // 3 - User is good to go, scrolls with no animation to it (the add to cart success modal actually break the animation)
      document.getElementById(CONFIG.STICKY_PARENT)?.scrollIntoView();
    }

    const isStickyBelowFold = Boolean(entry && entry?.boundingClientRect.top <= 0);

    onAddToCart(e, true, isStickyBelowFold);
  };

  /*
    if you are desktop show button immediately
  */
  const shouldShowInitially = isDesktop() || isPageOffset;
  const pageScrolled = ExecutionEnvironment.canUseDOM ? document.getElementById(ADD_TO_CART_BUTTON_ID)?.getBoundingClientRect().top! < 0 : false;
  const shouldShowAddToCartButton = entry && entry?.boundingClientRect.top > 0 && !inView && shouldShowInitially && !pageScrolled;

  // First sticky (top) should only show when scrolled PAST the original button
  const hasScrolledPastButton = pageScrolled || (entry && entry?.boundingClientRect.top <= 0);
  // First sticky shows when button is not in view AND second sticky is not showing (ensures mutual exclusivity)
  const isFirstStickyActive = entry && shouldShowInitially && !inView && !upperBoundary && !shouldShowAddToCartButton;
  // adding here for code reuse, move this to a new component if more features are added
  const productInfoWithAddToCartButton = () => (
    <>
      {featuredImage && (
        <div className={cn(css.featuredImage, css.productInfo)}>
          <img
            alt={featuredImage.type}
            src={constructMSAImageUrl(featuredImage.imageId, {
              width: 93,
              height: 62,
              autoCrop: true
            })}
            srcSet={`${constructMSAImageUrl(
              featuredImage.imageId,
              generateRetinaImageParams(
                {
                  width: 93,
                  height: 62,
                  autoCrop: true
                },
                2
              )
            )} 2x`}
          />
        </div>
      )}
      <div className={cn(css.productName, css.productInfo)}>{`${productDetail?.brandName} ${productDetail?.productName}`}</div>
      {pdpConfig.showFreeShipping && (
        <div className={cn(css.shipsFree, css.productInfo, 'bg-gray-100')}>
          <img src={shippingBox} alt="Ships Free" />
          ships free
        </div>
      )}
      <div className={cn(css.price, css.productInfo, { [css.hasDiscount]: hasDiscount })}>
        {price}
        {hasDiscount && <span className={css.originalPrice}>{originalPrice}</span>}
      </div>
      <div className={css.addToCart}>
        <button
          className={cn(cssAddToCart.cartButton, {
            [css.disabled]: isDisabled
          })}
          type="submit"
          form={CONFIG.BUY_BOX_FORM}
          data-stock-id={stockId}
          data-track-action="Product-Page"
          data-track-label="PrForm"
          data-track-value="Add-To-Cart-Sticky"
          data-test-id={testId('addToCartSticky')}
          onClick={addToCartAction}
          onMouseEnter={preventOnTouchDevice(onShowSelectSizeTooltip)}
          onMouseLeave={preventOnTouchDevice(onHideSelectSizeTooltip)}
          onFocus={preventOnTouchDevice(onShowSelectSizeTooltip)}
          onBlur={preventOnTouchDevice(onHideSelectSizeTooltip)}
        >
          {addToCartText}
        </button>
      </div>
    </>
  );

  return (
    <div className="isolate" ref={addToCartStickyRef}>
      <div
        data-test-id={testId('stickyAddToCartBanner')}
        aria-hidden={!entry}
        ref={addToCartStickyRef}
        className={cn(css.wrapper, {
          [css.shouldStick]: isFirstStickyActive,
          [css.stickToTheTop]: hasScrolledPastButton,
          [css.appDownloadBanner]: isAppAdvertisementShowing
        })}
      >
        <div className={css.container}>{productInfoWithAddToCartButton()}</div>
      </div>
      <div
        aria-hidden={!shouldShowAddToCartButton}
        className={cn(css.wrapper, css.stickToTheBottom, {
          [css.shouldStick]: shouldShowAddToCartButton,
          [css.appDownloadBanner]: isAppAdvertisementShowing
        })}
      >
        <div className={css.container}>{shouldShowAddToCartButton && productInfoWithAddToCartButton()}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  isAppAdvertisementShowing: state?.appAdvertisement?.isShowing
});

export default withErrorBoundary('AddToCartSticky', connect(mapStateToProps)(AddToCartSticky));
